import React from 'react';
import {
  NavLink as ReactRouterNavLink,
  NavLinkProps as ReactRouterNavLinkProps,
} from 'react-router-dom';
import s from '../Link/Link.scss';
import { capitalize, cn } from '@express-labs/raven-tools';
import {
  useHandleOnClickWithIntercept,
  useHandleOnKeyDownWithIntercept,
} from '../helpers/hook';

export type NavLinkProps = {
  /* Gives the link a "disabled" appearance however, unlike the html attribute "disabled", users are still able to tab-into a isAccessiblyDisabled Link and click on it. Any onClick or onKeyDown events while isAccessiblyDisabled is true are captured and ignored. */
  isAccessiblyDisabled?: boolean;
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (ev: React.KeyboardEvent<HTMLElement>) => void;
  /** Links come in three sizes. */
  size?: 'primary' | 'secondary' | 'body';
  /** The color theme for the link. */
  theme?: 'light' | 'dark';
  /** Force the link to display as "selected" or "inactive" */
  linkState?: 'pressed' | 'inactive';
  to: ReactRouterNavLinkProps['to'];
} & ReactRouterNavLinkProps;

const NavLink = ({
  children,
  className,
  isAccessiblyDisabled = false,
  linkState,
  onClick,
  onKeyDown,
  size = 'body',
  theme = 'dark',
  to,
  ...restProps
}: NavLinkProps) => {
  const handleOnClick = useHandleOnClickWithIntercept(
    isAccessiblyDisabled,
    onClick
  );

  const handleOnKeyDown = useHandleOnKeyDownWithIntercept(
    isAccessiblyDisabled,
    onKeyDown
  );

  return (
    <ReactRouterNavLink
      to={to}
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      className={cn(
        'rvn-ui--nav-link',
        'rvn-brander--exempt',
        s.aTag,
        s[`${size}${capitalize(theme)}`],
        isAccessiblyDisabled && s.inactive,
        linkState && s[linkState],
        className
      )}
      {...restProps}
    >
      {children}
    </ReactRouterNavLink>
  );
};

export default NavLink;
